import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionButtonGroup, Responsive, SettingsButtons, TabContainer } from '@components';
import { ApiEndpoint, FormActionType, FormType, Translation, WafTab } from '@enums';
import {
  useFetchFormList,
  useFormHandlers,
  useFormList,
  useFormLoadingStatus,
  useFormProcedure,
  useFormSubmissionStatus,
} from '@hooks';
import { getWafTabs, validateValueEqual } from '@utils';

import { WAFFormConfiguration, WAFRuleSets, WhiteListWAFForm } from '.';

export const Waf = () => {
  const formType = FormType.CdnWAF;

  const { t: tConfigurationButton } = useTranslation(Translation.Configuration, { keyPrefix: 'buttons' });

  const formProcedure = useFormProcedure(formType);
  const predefinedWAFSubmitting = useFormSubmissionStatus({ formType });
  const predefinedWAFLoading = useFormLoadingStatus({ formType });
  const formikRef = useRef<FormikProps<any> | null>(null);

  const [activeTab, setActiveTab] = useState<WafTab>(WafTab.CONFIGURATION);

  useFetchFormList({
    formType,
    globalOptions: {
      endpoint: ApiEndpoint.SCHEMA,
    },
    hookOptions: {
      resetFormTrigger: false,
    },
  });

  const options = useFormList({
    formType,
    endpoint: ApiEndpoint.SCHEMA,
  });

  const handleSaveSettings = useCallback(() => {
    formikRef?.current?.handleSubmit();
  }, []);

  const handleFormikInstance = (formik) => {
    formikRef.current = formik;
  };

  const useWAFFormHandler = (hideSaveButton = false) =>
    useFormHandlers({
      formType,
      handlerOptions: [
        {
          type: FormActionType.Save,
          function: handleSaveSettings,
          label: tConfigurationButton('saveSettings'),
          loading: predefinedWAFSubmitting,
          disabled: predefinedWAFLoading,
          ...(hideSaveButton && { display: false }),
        },
      ],
    });

  const WAFFormHandler = useWAFFormHandler();
  const WAFFormHandlerMobile = useWAFFormHandler(true);

  const contentConfig = useCallback(() => {
    return {
      [WafTab.CONFIGURATION]: (
        <WAFFormConfiguration formikRef={formikRef} onFormikInstance={handleFormikInstance} options={options} />
      ),
      [WafTab.WAF_RULE_SETS]: <WAFRuleSets options={options} />,
      [WafTab.WAF_WHITELIST]: <WhiteListWAFForm />,
    };
  }, [options]);

  const WafTabs = useMemo(() => getWafTabs(), [tConfigurationButton]);

  return (
    <div className="w-full flex flex-col flex-1 gap-6 sm:gap-8">
      <TabContainer<WafTab>
        tabs={WafTabs}
        onTabChange={setActiveTab}
        contentConfig={contentConfig()}
        adornmentClassName="flex-col gap-5"
        adornment={
          <>
            <div className="self-end">
              <Responsive showBelow="sm">
                {activeTab === WafTab.CONFIGURATION && (
                  <ActionButtonGroup actionHandlers={WAFFormHandlerMobile} procedure={formProcedure} />
                )}
              </Responsive>
            </div>

            <Responsive showAbove="sm">
              {activeTab === WafTab.CONFIGURATION && (
                <ActionButtonGroup actionHandlers={WAFFormHandler} procedure={formProcedure} />
              )}
            </Responsive>
          </>
        }
      />
      <Responsive showBelow="sm">
        {validateValueEqual(activeTab, WafTab.CONFIGURATION) && (
          <SettingsButtons handleSaveModal={handleSaveSettings} formType={formType} />
        )}
      </Responsive>
    </div>
  );
};
